import LocalRecommendations from "@reco/service/src/LocalRecommendations.ts";
import OneTrustConsent from "@reco/service/src/OneTrustConsent.ts";
import { getMultipleRecommendationsForPage } from "@reco/service/src/pageRecommendations";
import type { PageRecommendationsRequest, WidgetProps } from "@reco/service/src/types/pageRecos.ts";
import { NewPageType } from "@reco/service/src/utils/pageType";
import { getVisitHistory } from "@reco/service/src/utils/storage";
import type { LocaleContext } from "@reco/shared/src/composables/useLocale";
import type { ColorScheme } from "@reco/shared/src/constants.ts";
import RecoSliderWidget from "./components/Slider/RecoSliderWidget.vue";
import { mountWidget } from "./utils";

const getPageSections = (
  elementList: HTMLElement[],
  elements: HTMLElement[],
  isPDP: boolean,
): PageRecommendationsRequest["pageSections"] => {
  return elements.flatMap((elem, index) => {
    const position = index + 1;
    const parentNode = elem.parentNode as HTMLElement | null;
    const sectionName = isPDP ? parentNode?.classList[0] : parentNode?.dataset.selector;
    const sectionId = isPDP ? (sectionName ? `${sectionName}_${position}` : `notSet_${position}`) : parentNode?.id;
    if (!sectionId) {
      console.warn(
        `[Reco Widgets] Could not determine section id. Parent element needs a${isPDP ? " class" : "n id"} attribute.`,
      );
      return [];
    }

    elementList[position] = elem;

    return [
      {
        sectionId,
        sectionName: sectionName || undefined,
        defaultRecoPosition: isPDP ? position : undefined,
        defaultPagePosition: isPDP ? undefined : position,
        isContentChangeable: elem.dataset.isContentChangeable === "true",
        isReorderable: elem.dataset.isReorderable === "true",
        recoParams: {
          limit: Number(elem.dataset.limit) || 8,
          scenario: elem.dataset.type || "",
        },
      },
    ];
  });
};

const savePDPVisit = async (country: string, pageId: string | undefined | null, sectionData: WidgetProps) => {
  if (!import.meta.env.DEV && !import.meta.env.STORYBOOK) {
    const consent = await OneTrustConsent.getConsent(country);
    if (consent?.marketing) {
      new LocalRecommendations().saveVisit({
        contextId: pageId ?? undefined,
        scenario: sectionData.scenario,
        recommendationItems: sectionData.recommendations.map((rec) => ({
          itemId: rec.erpNumber ?? "",
          relevance: rec.relevance,
        })),
        categoryPath: sectionData.category,
        brand: sectionData.brand,
      });
    }
  }
};

/**
 * Mounts multiple page recommendations for the given elements at once.
 * @param elements The data-recommendation elements to mount recommendations on.
 * @param localeContext The locale context.
 * @param pageId The pageId.
 * @returns A promise that resolves when the recommendations are mounted.
 */
export const mountPageRecommendations = async (
  elements: HTMLElement[],
  localeContext: LocaleContext & { pageType: NewPageType },
  pageId: string | null | undefined,
): Promise<boolean> => {
  const elementList: HTMLElement[] = [];
  const isPDP = localeContext.pageType === NewPageType.PRODUCT_DETAIL_PAGE;

  const pageSections: PageRecommendationsRequest["pageSections"] = getPageSections(elementList, elements, isPDP);

  const viewedProducts: PageRecommendationsRequest["viewedProducts"] = getVisitHistory().map((record) => ({
    id: record.params.contextProduct,
    tradeType: record.params.tradeType,
    timestamp: record.timestamp,
  }));

  const pageData = await getMultipleRecommendationsForPage({
    country: localeContext.country,
    language: localeContext.language,
    touchPoint: "web",
    pageType: localeContext.pageType,
    pageId: pageId || "",
    pageSections,
    viewedProducts,
  });

  if (!pageData?.length) return false;

  pageData.forEach(async (sectionData) => {
    const position = sectionData.creativeSlot ?? sectionData.creativeSlotReco;
    if (position === undefined) return;

    const elem = elementList[position];

    if (sectionData.isProductsResponse && isPDP) savePDPVisit(localeContext.country, pageId, sectionData);

    if (elem) {
      mountWidget(RecoSliderWidget, elem, {
        ...sectionData,
        headingElement: elem.dataset.headingElement || "h2",
        showTitle: elem.dataset.showTitle === "false" ? false : true,
        colorScheme: (elem.dataset.colorScheme as ColorScheme) || undefined,
        creativeSlot: sectionData.creativeSlot ? String(sectionData.creativeSlot) : undefined,
        creativeSlotReco: sectionData.creativeSlotReco ? String(sectionData.creativeSlotReco) : undefined,
      });
    }
  });

  return true;
};
